import { stringify } from "query-string";
import { instance } from "../instance";
import { IAxiosError } from "../interfaces";
import { getError } from "../utils";

import {
  GetTeamAdsByTeamRequest,
  GetTeamPopupsByTeamRequest,
  IntervalGamePopulated,
  TeamAdsWithId,
  TeamPopupConfigPopulated,
} from "../../shared/interfaces";
import { PATH } from "../../shared/constants";

const isDevDomain = (): boolean => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  return host === "dev.mymomento.com"
    || host === "mom-dev-client.momento.pics"
    || host.endsWith(".mom-dev-client.momento.pics");
};

export const FAN_RECENT_GAMES_PAGE_SIZE = 20;

export type RecentGamesQuery = {
  start: number;
  teamId?: string;
  hostname?: string;
  limit?: number;
  offset?: number;
};

export const getRecentGamesRequest = async (
  query: RecentGamesQuery,
): Promise<IntervalGamePopulated[]> => {
  try {
    const includeDevGames = isDevDomain();
    const params = new URLSearchParams({
      recentGames: "true",
      start: String(query.start),
      archived: "false",
      excludeTemplates: "true",
      limit: String(query.limit ?? FAN_RECENT_GAMES_PAGE_SIZE),
      offset: String(query.offset ?? 0),
    });
    if (query.teamId) params.set("teamId", query.teamId);
    if (query.hostname) params.set("hostname", query.hostname);
    if (includeDevGames) params.set("includeDevGames", "true");
    const response = await instance.get(`${PATH.API}/${PATH.GAME}?${params}`);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getGameDataByIdRequest = async (
  gameId: string,
  options?: { fanLite?: boolean },
): Promise<IntervalGamePopulated> => {
  try {
    const qs = options?.fanLite ? "?fanLite=1" : "";
    const response = await instance.get(`${PATH.API}/${PATH.GAME}/${gameId}${qs}`);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export type GameMomentNotifySubscribePayload = {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  zipCode?: string;
  country?: string;
  birthDate?: string;
  teamOptIn?: boolean;
  sponsorOptIn?: boolean;
  thirdOptIn?: boolean;
  seasonTicketHolder?: boolean;
};

export const subscribeGameMomentNotifyRequest = async (
  gameId: string,
  payload: GameMomentNotifySubscribePayload,
): Promise<{ success: boolean }> => {
  try {
    const response = await instance.post(
      `${PATH.API}/${PATH.GAME}/${gameId}/moment-notify-subscribe`,
      payload,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getTeamAdsByTeamId = async (teamId: string): Promise<TeamAdsWithId> => {
  try {
    const request: GetTeamAdsByTeamRequest = {
      teamId,
    };

    const stringified = stringify(request, {
      skipNull: true,
    });
    const response = await instance.get(`${PATH.API}/${PATH.TEAM_ADS_FOR_TEAM}?${stringified}`);

    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};

export const getTeamPopupsByTeamId = async (
  teamId: string,
): Promise<TeamPopupConfigPopulated | null> => {
  try {
    const request: GetTeamPopupsByTeamRequest = { teamId };
    const stringified = stringify(request, { skipNull: true });
    const response = await instance.get(
      `${PATH.API}/${PATH.TEAM_POPUPS_FOR_TEAM}?${stringified}`,
    );
    return response.data;
  } catch (err) {
    const { response } = err as IAxiosError;
    throw new Error(getError(response));
  }
};
